import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription } from
"@/components/ui/dialog";
import { useEffect, useMemo, useState } from "react";
import { MessageSquare, FileText, CalendarCheck2, Wrench, GraduationCap, Globe } from "lucide-react";
import FreeAuditForm from "./FreeAuditForm";
import { motion } from "framer-motion";

interface ServiceItem {
  id: string;
  title: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  benefit: string;
  bullets: string[]; // 2 bullets
  details: string[]; // full explanation paragraphs
}

const services: ServiceItem[] = [
{
  id: "ai-chat-assistants",
  title: "AI Chat Assistants",
  Icon: MessageSquare,
  benefit: "24/7 answers, booking, and lead qualification",
  bullets: [
  "Cuts 2–6 hours of admin weekly",
  "Ideal for law, dental, gyms"],

  details: [
  "I build branded assistants that greet visitors on your website and social channels, answer common questions, qualify leads, and book appointments hands‑free.",
  "Assistants are trained on your policies, pricing, and tone. Handoffs to staff are clean when needed, and transcripts are logged for transparency.",
  "Expect faster response times, less inbox triage, and a better client experience—especially after hours."]

},
{
  id: "document-text-automation",
  title: "Document/Text Automation",
  Icon: FileText,
  benefit: "Summarize, draft, and route notes automatically",
  bullets: [
  "Learns your templates and tone",
  "Fewer manual errors, faster work"],

  details: [
  "We streamline note‑taking, intake summaries, drafts, and document generation.",
  "Your approved samples become the source of truth, so outputs stay consistent with your brand and compliance needs.",
  "Great for case updates, visit summaries, and templated comms that take too long today."]

},
{
  id: "smart-scheduling-workflows",
  title: "Smart Scheduling & Workflows",
  Icon: CalendarCheck2,
  benefit: "Reduce no‑shows with synced reminders & follow‑ups",
  bullets: [
  "Syncs with calendars/CRM",
  "Low‑lift onboarding"],

  details: [
  "Connect your Google/Outlook calendars and existing tools to automate reminders, confirmations, and rescheduling flows.",
  "We'll map your current process, then add the right nudges so clients show up and staff waste less time."]

},
{
  id: "custom-ai-solutions",
  title: "Custom AI Solutions",
  Icon: Wrench,
  benefit: "Tailored automations to your stack and industry",
  bullets: [
  "Automate repetitive tasks",
  "Improve productivity and ROI"],

  details: [
  "When you have a unique workflow, I build to fit—integrations, internal tools, and specialty automations that move the needle.",
  "We start small, measure impact, and scale what works across your team."]

},
{
  id: "workshops-training",
  title: "Workshops & Training",
  Icon: GraduationCap,
  benefit: "Hands-on learning for teams across AI tools and digital skills",
  bullets: [
  "AI literacy training and prompt engineering",
  "Custom tool workshops for your team"],

  details: [
  "I deliver hands-on workshops that train your team to confidently use AI tools and modern digital workflows in their daily work.",
  "Our training covers AI literacy fundamentals, effective prompt engineering techniques, and practical sessions with the specific tools that matter most to your industry—whether that's ChatGPT, document automation, scheduling tools, or custom solutions.",
  "Each workshop is tailored to your team's current skill level and business goals, ensuring everyone walks away with actionable knowledge they can apply immediately to save time and improve outcomes."]

},
{
  id: "websites-apps",
  title: "Websites & Apps",
  Icon: Globe,
  benefit: "Live in 5 days. Real code, fixed price.",
  bullets: [
    "Websites in 5 days, custom apps in days",
    "No drag-and-drop templates — real code",
  ],
  details: [
    "I build websites in 5 days and custom apps in days — not months. Real code, not drag-and-drop templates. Fixed price agreed before I start.",
    "Most sites are live before an agency has finished the proposal. Whether you need a marketing site, a client portal, or a fully custom web app, I scope it, price it, and ship it fast.",
    "Every project includes mobile-first design, performance optimization, and a CMS or admin panel if you need to update content yourself.",
  ],
},
];


const Services = () => {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Store the last selected service but don't auto-open on refresh
  useEffect(() => {
    const last = sessionStorage.getItem("lastService");
    if (last && services.some((s) => s.id === last)) {
      setSelectedId(last);
      // Removed auto-opening behavior to prevent popup after refresh
    }
  }, []);

  useEffect(() => {
    if (selectedId) sessionStorage.setItem("lastService", selectedId);
  }, [selectedId]);

  const selected = useMemo(
    () => services.find((s) => s.id === selectedId) || null,
    [selectedId]
  );

  return (
    <section id="services" className="py-16 md:py-24 bg-warm-cream">
      <div className="container mx-auto">
        <header className="mb-10 text-center">
          <h2 className="text-4xl md:text-5xl font-semibold font-display text-gray-900">Services</h2>
          <p className="text-lg md:text-xl text-gray-700 mt-3">Click "Details" to see the full breakdown and book your free audit.

          </p>
          

          
        </header>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.09 } } }}>
          
          {services.map(({ id, title, Icon, benefit, bullets }) =>
          <motion.div
            key={id}
            variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" as const } } }}
            whileHover={{ y: -6, boxShadow: "0 20px 40px rgba(0,0,0,0.12)" }}
            transition={{ type: "spring", stiffness: 380, damping: 22 }}
            className="h-full min-h-[320px]">
            
            <Card className="h-full flex flex-col">
              <CardHeader className="flex-row items-center gap-3">
                <span className="inline-flex size-10 items-center justify-center rounded-md bg-secondary">
                  <Icon className="text-primary" aria-hidden />
                </span>
                <CardTitle className="text-xl">{title}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0 flex-1 flex flex-col justify-between">
                <div>
                  <p className="text-base text-foreground font-medium">{benefit}</p>
                  <ul className="list-disc pl-5 space-y-1.5 mt-3 text-base text-cream-muted">
                    {bullets.slice(0, 2).map((p) =>
                    <li key={p}>{p}</li>
                    )}
                  </ul>
                </div>
                <div className="mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    aria-haspopup="dialog"
                    aria-controls={`service-${id}-dialog`}
                    onClick={() => {
                      setSelectedId(id);
                      setOpen(true);
                    }}>
                    
                    Details
                  </Button>
                </div>
              </CardContent>
            </Card>
            </motion.div>
          )}
        </motion.div>
      </div>

      <Dialog open={open} onOpenChange={(newOpen) => {
        setOpen(newOpen);
        // Reset form state when dialog closes to prevent validation issues
        if (!newOpen) {
          // Allow a small delay to let any pending validation settle
          setTimeout(() => {


            // The form will reset naturally when the dialog reopens
          }, 100);}}}>
        <DialogContent id={`service-${selected?.id}-dialog`} className="max-w-2xl">
          {selected &&
          <>
              <DialogHeader>
                <DialogTitle>{selected.title}</DialogTitle>
                <DialogDescription>
                  {selected.benefit}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-base text-cream-muted">
                {selected.details.map((d, i) =>
              <p key={i}>{d}</p>
              )}
              </div>
              <div className="mt-4">
                <h3 className="text-base font-semibold mb-2">Book Your Free Audit</h3>
                {/* Inline form - key prop ensures form resets when dialog reopens */}
                <FreeAuditForm key={open ? 'open' : 'closed'} onSubmitted={() => setOpen(false)} />
              </div>
            </>
          }
        </DialogContent>
      </Dialog>
    </section>);

};

export default Services;